import React, { useMemo, useState } from 'react';

import { enhancedApi } from '@/services/api/generated/firmsService';

import { FirmServicesStatusCard } from './FirmServicesStatusCard';
import { ServicesList } from './ServicesList';
import { TaskTemplatesSection } from './task-templates';
import { WorkflowsPanel } from './WorkflowsPanel';

interface FirmServicesWorkflowsConfigProps {
  firmId: string;
}

export const FirmServicesWorkflowsConfig: React.FC<
  FirmServicesWorkflowsConfigProps
> = ({ firmId }) => {
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null,
  );

  // Subscribe to firm services query - auto-updates when cache invalidates
  const { data: firmServices } = enhancedApi.useGetFirmServicesQuery({
    firmId,
  });

  // Derive the selected service from fresh query data
  const selectedService = useMemo(() => {
    if (!selectedServiceId || !firmServices) return null;
    return firmServices.find((s) => s.id === selectedServiceId) ?? null;
  }, [selectedServiceId, firmServices]);

  return (
    <div className="flex flex-col gap-6 h-full overflow-y-auto">
      {/* ─────────────────────────────────────────────────────────────────────
          SCOPE OF WORK SECTION
          Services & workflows that define the standard work for each case
      ───────────────────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 overflow-hidden">
        {/* Section Header */}
        <div className="px-5 py-4 border-b border-zinc-100 dark:border-zinc-800/60 bg-zinc-50/50 dark:bg-zinc-900/50">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
            Scope of Work
          </h2>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            Define which services and workflows apply to new cases by default.
            Toggle individual workflows on or off per service.
          </p>
        </div>

        {/* Status Banner - scoped to Scope of Work */}
        <div className="px-5 pt-4">
          <FirmServicesStatusCard firmId={firmId} />
        </div>

        {/* Services Configuration */}
        <div className="flex gap-6 p-5 min-h-[320px]">
          <div className="w-72 flex-shrink-0">
            <ServicesList
              firmId={firmId}
              selectedServiceId={selectedServiceId}
              onSelectService={(service) => setSelectedServiceId(service.id)}
            />
          </div>
          <div className="flex-1 min-w-0">
            {selectedService ? (
              <WorkflowsPanel firmId={firmId} service={selectedService} />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-zinc-400 dark:text-zinc-500">
                Select a service to configure workflows
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────────────
          FIRM TASK TEMPLATES SECTION
          Custom tasks that can be auto-created or manually applied
      ───────────────────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 overflow-hidden">
        <div className="p-5">
          <TaskTemplatesSection firmId={firmId} />
        </div>
      </section>
    </div>
  );
};
